import { isFullConfig, phasesFor, type PipelineProfile } from "@procurement-agents/scope-api";
import { PHASES } from "../labels";
import { DraftGate, useAgents } from "./AgentsApp";
import { STEPS } from "./labels";

/** The graph as a row of steps: skeleton steps are fixed; optional steps toggle a profile flag (SPEC_SLICE6 section 5). */
export function Pipeline() {
  const { shown, editable, apply, draft } = useAgents();
  if (!isFullConfig(shown)) return <p className="muted">Not visible to you.</p>;
  const profile = shown.profile;
  const set = (flag: keyof PipelineProfile, value: boolean | string) => void apply([{ op: "replace", path: `/profile/${flag}`, value }]);
  const kept = new Set(phasesFor(profile, PHASES.map((p) => p.id)));
  const gone = PHASES.filter((p) => !kept.has(p.id) && p.id !== "complete");
  const errors = draft?.errors ?? [];
  const warnings = draft?.warnings ?? [];
  return (
    <section className="pipeline">
      <DraftGate />
      <ol className="pipe-row" aria-label="Pipeline steps">
        {STEPS.map((s) => {
          const on = s.flag ? (s.flag === "structure_decision" ? true : !!profile[s.flag]) : true;
          const disabledBy = s.needs && !profile[s.needs] ? s.needs : null;
          return (
            <li key={s.id} className={["pipe-step", s.fixed ? "fixed" : "optional", on && !disabledBy ? "on" : "off"].join(" ")} title={s.note || undefined}>
              <span className="pipe-label">{s.label}</span>
              {s.fixed && <span className="muted small">fixed</span>}
              {s.flag === "structure_decision" && (
                <select aria-label="Structure decision" value={profile.structure_decision} disabled={!editable} onChange={(e) => set("structure_decision", e.target.value)}>
                  <option value="always">always ask</option>
                  <option value="on_disagreement">on disagreement</option>
                </select>
              )}
              {s.flag && s.flag !== "structure_decision" && (
                <label className="switch">
                  <input type="checkbox" role="switch" aria-label={s.label} checked={!!profile[s.flag]} disabled={!editable || !!disabledBy} onChange={(e) => set(s.flag as keyof PipelineProfile, e.target.checked)} />
                  <span className="small">{disabledBy ? `needs ${disabledBy}` : profile[s.flag] ? "on" : "off"}</span>
                </label>
              )}
            </li>
          );
        })}
      </ol>
      <p className="muted small">The stepper on every run started with this configuration shows: {PHASES.filter((p) => kept.has(p.id)).map((p) => p.label).join(" → ")}.{gone.length > 0 && <> Phases that disappear: <strong>{gone.map((p) => p.label).join(", ")}</strong>.</>}</p>
      {draft && (
        <aside className={`validation-panel ${errors.filter((e) => !e.startsWith("no preview")).length ? "bad" : "good"}`} aria-live="polite">
          {errors.length ? <><strong>Before publishing:</strong><ul>{errors.map((e, i) => <li key={i}>{e}</li>)}</ul></> : <span>Valid: this draft can be published.</span>}
          {warnings.length > 0 && <ul className="warnings">{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>}
        </aside>
      )}
      <dl className="dl notes">
        {STEPS.filter((s) => s.note).map((s) => <div key={s.id}><dt>{s.label}</dt><dd className="small muted">{s.note}</dd></div>)}
      </dl>
    </section>
  );
}
